import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { CONTACT } from "@/lib/api";

const links = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_30px_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      {/* Top contact strip */}
      <div className="bg-[var(--navy)] text-white text-xs md:text-sm" data-testid="top-contact-bar">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-9 flex items-center justify-between gap-4">
          <a
            href={`tel:+${CONTACT.phoneIntl}`}
            className="flex items-center gap-2 font-semibold hover:text-[var(--gold)] transition-colors"
            data-testid="top-phone"
          >
            <Phone weight="fill" size={14} className="text-[var(--gold)]" />
            <span className="hidden sm:inline">Call:</span>
            <span>+91 {CONTACT.phone}</span>
            <span className="hidden md:inline text-white/50">/</span>
            <span className="hidden md:inline">+91 {CONTACT.phoneAlt}</span>
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            className="flex items-center gap-2 font-semibold hover:text-[var(--gold)] transition-colors truncate max-w-[60%] sm:max-w-none"
            data-testid="top-email"
          >
            <EnvelopeSimple weight="fill" size={14} className="text-[var(--gold)]" />
            <span className="hidden sm:inline">Email:</span>
            <span className="truncate">{CONTACT.email}</span>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="brand-logo">
          <img
            src="/logo.png?v=3"
            alt="Quick Maid Services Pune"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-semibold text-[var(--ink)]/80 hover:text-[var(--navy)] transition-colors relative group"
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-[var(--gold)] group-hover:w-full transition-[width] duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:+${CONTACT.phoneIntl}`}
            className="hidden sm:flex items-center gap-2 text-sm font-bold text-[var(--navy)]"
            data-testid="nav-phone"
          >
            <Phone weight="fill" size={16} className="text-[var(--gold)]" />
            {CONTACT.phone}
          </a>
          <button
            onClick={onBook}
            className="hidden md:inline-flex items-center rounded-full bg-[var(--navy)] text-white text-sm font-bold px-5 py-2.5 hover:bg-[var(--navy-deep)] transition-colors"
            data-testid="nav-book-now"
          >
            Book Now
          </button>
          <button
            className="md:hidden p-2 text-[var(--navy)]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-t border-black/5"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base font-semibold text-[var(--ink)] border-b border-slate-100"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="mt-3 rounded-full bg-[var(--navy)] text-white font-bold px-5 py-3"
                data-testid="mobile-book-now"
              >
                Book Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
